import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import dayjsTimezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import i18n from '../i18n';

export const DASHBOARD_DATE_FORMATS = { default: 0, dayMonthYear: 1, monthDayYear: 2, dayMonthNameYear: 3, dayMonthYearTime: 4, monthDayYearTime: 5, dayMonthNameYearTime: 6 };
const DATE_FORMAT_PATTERNS = { 1: 'DD/MM/YYYY', 2: 'MM/DD/YYYY', 3: 'DD - MMMM - YYYY', 4: 'DD/MM/YYYY', 5: 'MM/DD/YYYY', 6: 'DD - MMMM - YYYY' };
const TIME_PATTERN_12_HOUR = 'h:mm A';
const TIME_PATTERN_24_HOUR = 'HH:mm';

let subscriptionDateFormat = DASHBOARD_DATE_FORMATS.default;

const PICKER_DATE_FORMAT = 'YYYY-MM-DD';
const PICKER_TIME_FORMAT = 'HH:mm:ss';
const PICKER_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const LRI = '\u2066';
const PDI = '\u2069';

export const DATE_INTERVALS = {
  today: 1,
  yesterday: 2,
  tomorrow: 3,
  thisWeek: 4,
  thisMonth: 5,
  thisYear: 6,
  lastMonth: 7,
  next30Days: 8,
  custom: 9
};

dayjs.extend(LocalizedFormat);
dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

function languageCode(language) {
  const value = language || i18n.language || 'en';
  return String(value).split(/[_-]/)[0];
}

function isArabicLanguage(language) {
  return languageCode(language) === 'ar';
}

export function setSubscriptionDateFormat(dateFormat) {
  const code = Number(dateFormat);
  subscriptionDateFormat = DATE_FORMAT_PATTERNS[code] ? code : DASHBOARD_DATE_FORMATS.default;
}

export function getSubscriptionDateFormat() {
  return subscriptionDateFormat;
}

function hasTime(dateFormat) {
  return dateFormat >= DASHBOARD_DATE_FORMATS.dayMonthYearTime && dateFormat <= DASHBOARD_DATE_FORMATS.dayMonthNameYearTime;
}

function isDateOnly(dateFormat) {
  return dateFormat >= DASHBOARD_DATE_FORMATS.dayMonthYear && dateFormat <= DASHBOARD_DATE_FORMATS.dayMonthNameYear;
}

function formatDateTimeByPattern(date, style, dateFormat, language) {
  const localized = dayjs(date).tz(timezone).locale(languageCode(language));
  const datePart = localized.format(DATE_FORMAT_PATTERNS[dateFormat]);
  const timePart = hasTime(dateFormat) ? localized.locale('en').format(TIME_PATTERN_12_HOUR) : formatDateTime(date, 'time', language, DASHBOARD_DATE_FORMATS.default);

  let formatted;
  if (style === 'date') {
    formatted = datePart;
  } else if (style === 'time') {
    formatted = timePart;
  } else {
    formatted = isDateOnly(dateFormat) ? datePart : `${datePart} ${timePart}`;
  }

  return isArabicLanguage(language) ? `${LRI}${formatted}${PDI}` : formatted;
}

function formatArabicDateTime(date, style = 'dateTime') {
  const d = dayjs(date).tz(timezone);
  const pad = (value) => String(value).padStart(2, '0');
  const dateStr = `${d.year()}-${pad(d.month() + 1)}-${pad(d.date())}`;
  const hours = d.hour();
  const minutes = d.minute();
  const ampm = hours < 12 ? 'ص' : 'م';
  const h12 = hours % 12 || 12;
  const timeStr = `${h12}:${pad(minutes)} ${ampm}`;

  let formatted;
  if (style === 'date') {
    formatted = dateStr;
  } else if (style === 'time') {
    formatted = timeStr;
  } else {
    formatted = `${dateStr} ${timeStr}`;
  }

  return `${LRI}${formatted}${PDI}`;
}

function formatDateTime(date, style = 'dateTime', language, dateFormat = subscriptionDateFormat) {
  if (!date) return null;

  if (DATE_FORMAT_PATTERNS[dateFormat] && (style !== 'time' || hasTime(dateFormat))) {
    return formatDateTimeByPattern(date, style, dateFormat, language);
  }

  if (isArabicLanguage(language)) {
    return formatArabicDateTime(date, style);
  }

  const langCode = languageCode(language);
  const options = { timeZone: timezone };
  if (style === 'date') options.dateStyle = 'short';
  else if (style === 'time') options.timeStyle = 'short';
  else {
    const dateFormatter = new Intl.DateTimeFormat(langCode, {
      dateStyle: 'short',
      timeZone: timezone
    });
    const timeFormatter = new Intl.DateTimeFormat(langCode, {
      timeStyle: 'short',
      timeZone: timezone
    });
    return `${dateFormatter.format(date)} ${timeFormatter.format(date)}`;
  }

  return new Intl.DateTimeFormat(langCode, options).format(date);
}

export function dateFormatForPicker(language) {
  if (DATE_FORMAT_PATTERNS[subscriptionDateFormat]) {
    return DATE_FORMAT_PATTERNS[subscriptionDateFormat];
  }
  const langCode = languageCode(language);
  return langCode === 'tr' ? 'DD.MM.YYYY' : 'MM/DD/YY';
}

// Returns undefined for the default format so pickers keep their locale based format.
export function dateTimeFormatForPicker() {
  if (!DATE_FORMAT_PATTERNS[subscriptionDateFormat]) return undefined;
  return `${DATE_FORMAT_PATTERNS[subscriptionDateFormat]} ${timeFormatForPicker() ?? TIME_PATTERN_24_HOUR}`;
}

// Returns undefined unless the subscription uses a 12-hour format.
export function timeFormatForPicker() {
  return hasTime(subscriptionDateFormat) ? TIME_PATTERN_12_HOUR : undefined;
}

// Returns null when the subscription format does not define the requested part, so callers can keep their own locale formatting.
export function localDateToSubscriptionFormat(date, style = 'dateTime') {
  const pattern = DATE_FORMAT_PATTERNS[subscriptionDateFormat];
  if (!date || !pattern) return null;
  const localized = dayjs(date).tz(timezone).locale(languageCode());
  const timePart = localized.locale('en').format(TIME_PATTERN_12_HOUR);
  if (style === 'time') return hasTime(subscriptionDateFormat) ? timePart : null;
  if (style === 'date' || isDateOnly(subscriptionDateFormat)) return localized.format(pattern);
  return `${localized.format(pattern)} ${timePart}`;
}

export function dateTimeIsoToLocalizedWithTimeZone(isoText, language) {
  const date = isoText ? dayjs.utc(isoText).tz(timezone).toDate() : null;
  return formatDateTime(date, 'dateTime', language);
}

export function dateTimeIsoToLocalized(isoText, language) {
  const date = isoText ? dayjs(isoText).toDate() : null;
  return formatDateTime(date, 'dateTime', language);
}

export function dateIsoToLocalized(isoText, language) {
  const date = isoText ? dayjs(isoText).toDate() : null;
  return formatDateTime(date, 'date', language);
}

export function timeIsoToLocalized(isoText, language) {
  const date = isoText ? dayjs(isoText).toDate() : null;
  return formatDateTime(date, 'time', language);
}

// Table format 0 follows the subscription date format.
export function dashboardDateToLocalized(isoText, dateFormat, dateView, language) {
  if (!isoText) return null;
  const pattern = DATE_FORMAT_PATTERNS[dateFormat];
  if (!pattern) {
    return dateView ? dateIsoToLocalized(isoText, language) : dateTimeIsoToLocalized(isoText, language);
  }
  const date = dayjs(isoText).locale(languageCode(language));
  const datePart = date.format(pattern);
  if (dateView || isDateOnly(dateFormat)) return datePart;
  const timePart = hasTime(dateFormat) ? date.locale('en').format(TIME_PATTERN_12_HOUR) : timeIsoToLocalized(isoText, language);
  return `${datePart} ${timePart}`;
}

export function subscriptionDateToLocalized(isoText, language, dateOnly = false) {
  return dashboardDateToLocalized(isoText, getSubscriptionDateFormat(), dateOnly || isDateOnly(getSubscriptionDateFormat()), language);
}

export function convertDateObject(isoText) {
  return dayjs(isoText);
}

export function convertDateIsoFormat(date) {
  return dayjs(date).format(PICKER_DATE_FORMAT);
}

export function convertDateTimeIsoFormat(date) {
  return dayjs(date).format(PICKER_DATE_TIME_FORMAT);
}

export function convertTimeIsoFormat(date) {
  return dayjs(date).format(PICKER_TIME_FORMAT);
}

export function startOfDate(isoText, type) {
  const date = convertDateObject(isoText);
  return date.startOf(type);
}

export function endOfDate(isoText, type) {
  const date = convertDateObject(isoText);
  return date.endOf(type);
}

export function prepareDateFormatParams(params) {
  const dateInterval = params.dateInterval;
  let dataParams = {};

  if (dateInterval) {
    if (DATE_INTERVALS.custom === dateInterval) {
      dataParams = calculateCustomDates(params.startDate, params.endDate);
    } else {
      dataParams = calculateDates(dateInterval);
    }
  }

  return dataParams;
}

export function calculateCustomDates(startDateText, endDateText) {
  const startDate = startOfDate(startDateText, 'day');
  const endDate = endOfDate(endDateText, 'day');

  return { startDate: convertDateTimeIsoFormat(startDate), endDate: convertDateTimeIsoFormat(endDate) };
}

export function calculateDates(dateInterval) {
  const now = dayjs();
  let startDate = now.startOf('day');
  let endDate = now.endOf('day');

  switch (dateInterval) {
    case DATE_INTERVALS.today:
      break;
    case DATE_INTERVALS.yesterday:
      startDate = startDate.subtract('1', 'day');
      endDate = endDate.subtract('1', 'day');
      break;
    case DATE_INTERVALS.tomorrow:
      startDate = startDate.add('1', 'day');
      endDate = endDate.add('1', 'day');
      break;
    case DATE_INTERVALS.thisWeek:
      startDate = startDate.startOf('week');
      break;
    case DATE_INTERVALS.thisMonth:
      startDate = startDate.startOf('month');
      break;
    case DATE_INTERVALS.thisYear:
      startDate = startDate.startOf('year');
      break;
    case DATE_INTERVALS.lastMonth:
      startDate = startDate.subtract('1', 'month');
      break;
    case DATE_INTERVALS.next30Days:
      endDate = endDate.add('30', 'day');
      break;
    default:
      break;
  }

  return { startDate: convertDateTimeIsoFormat(startDate), endDate: convertDateTimeIsoFormat(endDate) };
}
